import { useState, useEffect, useMemo } from "react";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
// import Icon from "@mui/material/Icon";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";

// import SoftBox from "components/SoftBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import routes from "routes";
import {
  useSoftUIController,
  setMiniSidenav,
  // setOpenConfigurator,
} from "context";
import brand from "assets/images/logo.svg";
import { ProtectedRoute } from "ProtectedRoute";
import useNavRoutes from "nav.routes";
import i18n from "i18next"; // Assuming i18next is used for i18n
import GoogleAnalytics from "./utils/googleAanalytics";
import { initHotjar } from "./utils/hotjar";
import { usePermissions } from "context/permissions-context/permissions.context";
import NotificationService from "./utils/notificationService";
import InstallPWA from "components/InstallPWA";
import { useAuth } from "context/auth-context/auth.context";

export default function App() {
  const navRoutes = useNavRoutes();
  const navigate = useNavigate();

  const [controller, dispatch] = useSoftUIController();
  // eslint-disable-next-line
  const { miniSidenav, layout, openConfigurator, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const location = useLocation();
  const { userPermissions } = usePermissions();

  // Initialize cache for RTL support
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Set the language direction based on current language (LTR for English/French, RTL for Arabic)
  useEffect(() => {
    const currentDirection = i18n.language === "ar" ? "rtl" : "ltr";
    document.body.setAttribute("dir", currentDirection);
    // eslint-disable-next-line
  }, [i18n.language]);

  useEffect(() => {
    initHotjar();
  }, []);

  // useEffect(() => {
  //   const token = localStorage.getItem("token");
  //   if (token && NotificationService.isSupported()) {
  //     NotificationService.init(token);
  //   }
  // }, []);

  const { user } = useAuth();

  useEffect(() => {
    if (NotificationService.isSupported()) {
      const token = user?.token || localStorage.getItem("token");
      console.log("Syncing notifications. Token present:", !!token);
      NotificationService.init(token);
      NotificationService.clearBadge();
    }
  }, [user]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // const handleConfiguratorOpen = () =>
  //   setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const isTokenExpired = () => {
    const token = localStorage.getItem("token");
    if (!token) return true;
    const tokenData = JSON.parse(atob(token.split(".")[1]));
    const now = Date.now() / 1000;
    return tokenData.exp < now;
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    const isTokenExpired = () => {
      if (!token) return true;
      try {
        const { exp } = JSON.parse(atob(token.split(".")[1]));
        return Date.now() / 1000 > exp;
      } catch {
        return true;
      }
    };

    // Handle expired token
    if (token && isTokenExpired()) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate("/home");
      return;
    }

    // Handle automatic logout at token expiry
    if (token) {
      try {
        const { exp } = JSON.parse(atob(token.split(".")[1]));
        const timeUntilExpiry = exp * 1000 - Date.now();
        const logoutTimer = setTimeout(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/home");
        }, timeUntilExpiry);
        return () => clearTimeout(logoutTimer);
      } catch {
        // malformed token
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/home");
      }
    }

    // Redirect after login based on permissions
    if (
      !isTokenExpired() &&
      userPermissions.includes("dashboard_access") &&
      (location.pathname === "/authentication/connexion" ||
        location.pathname === "/home")
    ) {
      window.location.href = "/statistiques/statistiques-generales";
    } else if (
      !isTokenExpired() &&
      userPermissions.length > 0 &&
      !userPermissions.includes("dashboard_access") &&
      (location.pathname === "/authentication/connexion" ||
        location.pathname === "/home")
    ) {
      window.location.href = "/gestion-des-commandes/commandes-encours";
    } else if (
      !token &&
      location.pathname !== "/authentication/connexion" &&
      location.pathname !== "/home" &&
      location.pathname !== "/privacy"
    ) {
      navigate("/home");
    }
    // eslint-disable-next-line
  }, [location.pathname, navigate]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        // Check if the route requires protection based on token and permissions
        if (route.protected) {
          // Use the ProtectedRoute component and pass the required permission
          return (
            <Route
              key={route.key}
              element={
                <ProtectedRoute requiredPermission={route.requiredPermission} />
              }
            >
              <Route path={route.route} element={route.component} />
            </Route>
          );
        }
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  // const configsButton = (
  //   <SoftBox
  //     display="flex"
  //     justifyContent="center"
  //     alignItems="center"
  //     width="3.5rem"
  //     height="3.5rem"
  //     bgColor="white"
  //     shadow="sm"
  //     borderRadius="50%"
  //     position="fixed"
  //     right="2rem"
  //     bottom="2rem"
  //     zIndex={99}
  //     color="dark"
  //     sx={{ cursor: "pointer" }}
  //     onClick={handleConfiguratorOpen}
  //   >
  //     <Icon fontSize="default" color="inherit">
  //       settings
  //     </Icon>
  //   </SoftBox>
  // );

  return i18n.language === "ar" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <GoogleAnalytics />
        <CssBaseline />
        {layout === "dashboard" && pathname !== "/privacy" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={brand}
              brandName="Titan LCD"
              routes={navRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {/* {configsButton} */}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route
            path="*"
            element={
              <Navigate
                to={
                  isTokenExpired()
                    ? "/home"
                    : "/statistiques/statistiques-generales"
                }
              />
            }
          />
        </Routes>
        <InstallPWA />
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <GoogleAnalytics />
      <CssBaseline />
      {layout === "dashboard" && pathname !== "/privacy" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brand}
            brandName="Titan LCD Dashboard"
            routes={navRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {/* {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route
          path="*"
          element={
            <Navigate
              to={
                isTokenExpired()
                  ? "/home"
                  : "/statistiques/statistiques-generales"
              }
            />
          }
        />
      </Routes>
      <InstallPWA />
    </ThemeProvider>
  );
}
